import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ADMIN } from '@/constants/testIds';

const EMPTY = { name: '', resource_type: 'room', clinic_id: '', description: '', active: true };

export default function RoomsTab() {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get('/rooms'), api.get('/clinics')]);
      setRows(r.data); setClinics(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.patch(`/rooms/${editing.id}`, form);
      else await api.post('/rooms', form);
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`/rooms/${r.id}`); load();
  };
  const clinicName = (id) => clinics.find(c => c.id === id)?.name || '—';

  return (
    <div>
      <AdminHeader
        title="Resources"
        description="Rooms, equipment, trial devices, and vehicles for home visits."
        onCreate={openCreate}
        createLabel="New Resource"
        createTestId={ADMIN.createButton('room')}
      />
      <AdminTable
        loading={loading} rows={rows}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'resource_type', label: 'Type', render: (r) => (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 rounded font-semibold">{r.resource_type}</span>
          )},
          { key: 'clinic', label: 'Clinic', render: (r) => clinicName(r.clinic_id) },
          { key: 'description', label: 'Description' },
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Resource' : 'New Resource'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9" data-testid="room-field-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.resource_type} onValueChange={(v) => setForm({ ...form, resource_type: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="room">Room</SelectItem>
                  <SelectItem value="equipment">Equipment</SelectItem>
                  <SelectItem value="trial_device">Trial Device</SelectItem>
                  <SelectItem value="vehicle">Vehicle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Clinic</Label>
              <Select value={form.clinic_id || ''} onValueChange={(v) => setForm({ ...form, clinic_id: v })}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent>
                  {clinics.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
